import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, ChevronRight, Plus, Search, TrendingUp } from 'lucide-react'
import { api } from '@/lib/api'
import {
  ACTIVE_FLOW_STAGES,
  STAGE_LABELS,
  TERMINAL_STAGES,
  type PipelineStage,
  type ServiceLine,
} from '@/lib/stages'

interface ClientStubLite {
  id: string
  displayName: string
  email?: string | null
  phoneE164?: string | null
}

interface Opportunity {
  id: string
  title: string
  stage: PipelineStage
  serviceLine: string
  monetaryValueCents: number
  probability: number
  tags: string[]
  clientStubId: string
  clientStub?: ClientStubLite | null
  updatedAt: string
}

interface ByStageBucket {
  items: Opportunity[]
  hasMore: boolean
  totalCount: number
  totalCents: number
}

interface ForecastResponse {
  totalPipelineCents: number
  weightedForecastCents: number
  openCount: number
  wonCount: number
  wonCents: number
  lostCount: number
  conversionRate: number
  avgDealSizeCents: number
}

interface LostReason {
  id: string
  name: string
  active: boolean
}

// Orden de columnas mostrado de izquierda a derecha (Sprint 13: 9 stages
// espejo de JR2_OS; EN_INVERSION entre Propuesta enviada y Ganada). PERDIDA va
// último y detrás de un toggle: por defecto distrae más de lo que aporta.
const VISIBLE_STAGES: PipelineStage[] = [
  'LEAD',
  'PROSPECTO',
  'OPORTUNIDAD',
  'PROPUESTA_EN_PREPARACION',
  'PROPUESTA_ENVIADA',
  'EN_INVERSION',
  'GANADA',
  'EN_ESPERA',
]

function eur(cents: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

function emptyStateText(stage: PipelineStage): string {
  if (stage === 'LEAD') return 'Sin leads todavía'
  if (stage === 'GANADA') return 'Sin cierres todavía'
  if (stage === 'PERDIDA') return 'Sin pérdidas'
  return 'Sin oportunidades en este stage'
}

export function PipelinePage() {
  const [byStage, setByStage] = useState<Record<string, ByStageBucket>>({})
  const [forecast, setForecast] = useState<ForecastResponse | null>(null)
  const [showLost, setShowLost] = useState(false)
  const [filterServiceLine, setFilterServiceLine] = useState<string>('')
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>([])
  const [query, setQuery] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [lostFor, setLostFor] = useState<Opportunity | null>(null)
  const [confirmWonFor, setConfirmWonFor] = useState<Opportunity | null>(null)
  const [lostReasons, setLostReasons] = useState<LostReason[]>([])
  // ids de opps con un POST /stage en vuelo — disable + spinner en el select.
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set())

  // Contador monotónico de loads para descartar respuestas stale. Si el
  // usuario cambia un stage justo cuando llega un poll viejo, sólo se aplica
  // la última respuesta. Patrón request-id evita el "rebote" visual.
  const loadSeqRef = useRef(0)
  const inFlightAbortRef = useRef<AbortController | null>(null)

  async function load() {
    setErr(null)
    const mySeq = ++loadSeqRef.current
    inFlightAbortRef.current?.abort()
    const ac = new AbortController()
    inFlightAbortRef.current = ac
    try {
      const [r, f] = await Promise.all([
        api<{ byStage: Record<string, ByStageBucket> }>(
          '/v1/marketing/opportunities/by-stage',
          { signal: ac.signal },
        ),
        api<ForecastResponse>('/v1/marketing/opportunities/forecast', {
          signal: ac.signal,
        }),
      ])
      // Descarta si llegó otra respuesta más reciente.
      if (mySeq !== loadSeqRef.current) return
      setByStage(r.byStage)
      setForecast(f)
    } catch (e) {
      if (mySeq !== loadSeqRef.current) return
      if ((e as { name?: string })?.name === 'AbortError') return
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  async function loadLostReasons() {
    try {
      const r = await api<{ items: LostReason[] }>('/v1/marketing/lost-reasons')
      setLostReasons(r.items.filter((x) => x.active))
    } catch {
      // silencioso: el modal de pérdida muestra el error si lista vacía.
    }
  }

  // Catálogo de líneas de servicio — read-only, mirror de JR2_OS. Se usa para
  // el filtro y para el select del modal de creación. includeInactive=true
  // (Sprint 19 — F4): el filtro debe poder aislar opps de líneas retiradas;
  // el modal de creación filtra a solo activas por su cuenta.
  async function loadServiceLines() {
    try {
      const r = await api<{ items: ServiceLine[] }>(
        '/v1/marketing/service-lines?includeInactive=true',
      )
      setServiceLines(r.items)
    } catch {
      // silencioso: sin catálogo el filtro queda vacío y el modal usa fallback.
    }
  }

  // Ref espejo del estado que pausa el polling. Se actualiza en cada render y
  // el tick del interval lo lee: así el efecto puede tener deps [] de verdad
  // (un único interval estable) sin capturar estado stale en el closure.
  // Con las deps en el array, cada mutación re-disparaba el efecto y lanzaba
  // un load() incondicional en paralelo con el POST /stage — el "rebote"
  // visual que el patrón request-id intenta evitar.
  const pollPausedRef = useRef(false)
  pollPausedRef.current =
    creating || lostFor !== null || confirmWonFor !== null || pendingIds.size > 0

  useEffect(() => {
    void load()
    void loadLostReasons()
    void loadServiceLines()
    const t = setInterval(() => {
      // Pausa polling si la pestaña no está visible, hay modales abiertos o
      // hay una mutación en vuelo (evita pisar el optimistic update).
      if (document.visibilityState !== 'visible') return
      if (pollPausedRef.current) return
      void load()
    }, 12000)
    return () => clearInterval(t)
    // deps [] intencional: interval único durante toda la vida del componente;
    // el tick lee pollPausedRef (actualizado en cada render) en vez de estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filtrado client-side por línea de servicio y query. Mantiene la carga
  // /by-stage barata sin proliferar params; el cap por columna es 50 así que
  // filtrar en cliente sobre ese pool basta para MVP.
  //
  // Cabeceras honestas (Sprint 19 — F1):
  //   - SIN filtros: los totales de columna (count y suma €) son los del
  //     SERVIDOR (totalCount/totalCents del bucket), no el recuento del
  //     top-50 renderizado.
  //   - CON filtros: los totales se calculan sobre el subconjunto filtrado
  //     (y la cabecera lo indica), porque el servidor no conoce el filtro.
  //   - hasMore pasa SIEMPRE crudo del servidor: el aviso de truncado debe
  //     aparecer exactamente cuando el stage tiene más opps que las top-50
  //     cargadas (antes se suprimía justo cuando el filtro descartaba items).
  const filteringActive = Boolean(filterServiceLine || query.trim())

  const filteredByStage = useMemo(() => {
    if (!filteringActive) return byStage
    const out: Record<string, ByStageBucket> = {}
    const q = query.trim().toLowerCase()
    for (const [stage, bucket] of Object.entries(byStage)) {
      const items = bucket.items.filter((it) => {
        if (filterServiceLine && it.serviceLine !== filterServiceLine) return false
        if (q) {
          const hay = `${it.title} ${it.clientStub?.displayName ?? ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      const totalCents = items.reduce((acc, it) => acc + it.monetaryValueCents, 0)
      out[stage] = {
        items,
        hasMore: bucket.hasMore,
        totalCount: items.length,
        totalCents,
      }
    }
    return out
  }, [byStage, filterServiceLine, query, filteringActive])

  const stagesToShow = useMemo(
    () => (showLost ? [...VISIBLE_STAGES, 'PERDIDA' as const] : VISIBLE_STAGES),
    [showLost],
  )

  async function changeStage(opp: Opportunity, toStage: PipelineStage) {
    if (opp.stage === toStage) return // no-op
    // Confirm explícito para terminales — irreversibles a efectos de funnel,
    // disparan TagRules/webhooks. PERDIDA además abre el modal de razón.
    if (toStage === 'PERDIDA') {
      setLostFor(opp)
      return
    }
    if (toStage === 'GANADA') {
      setConfirmWonFor(opp)
      return
    }
    await doChangeStage(opp, toStage)
  }

  async function doChangeStage(
    opp: Opportunity,
    toStage: PipelineStage,
    lostReasonId?: string,
  ) {
    setPendingIds((s) => {
      const n = new Set(s)
      n.add(opp.id)
      return n
    })
    // Optimistic: mueve la card al toStage local antes del POST. Si el POST
    // falla, hacemos rollback recargando.
    const prevByStage = byStage
    setByStage((cur) => {
      const next = { ...cur }
      const src = next[opp.stage]
      const dst = next[toStage]
      if (src) {
        next[opp.stage] = {
          ...src,
          items: src.items.filter((it) => it.id !== opp.id),
          totalCount: Math.max(0, src.totalCount - 1),
          totalCents: Math.max(0, src.totalCents - opp.monetaryValueCents),
        }
      }
      const moved = { ...opp, stage: toStage }
      if (dst) {
        next[toStage] = {
          ...dst,
          items: [moved, ...dst.items],
          totalCount: dst.totalCount + 1,
          totalCents: dst.totalCents + opp.monetaryValueCents,
        }
      } else {
        next[toStage] = {
          items: [moved],
          hasMore: false,
          totalCount: 1,
          totalCents: opp.monetaryValueCents,
        }
      }
      return next
    })
    try {
      await api(`/v1/marketing/opportunities/${opp.id}/stage`, {
        method: 'POST',
        body: JSON.stringify({
          toStage,
          ...(lostReasonId ? { lostReasonId } : {}),
        }),
      })
      // Reload tras éxito para reconciliar (forecast, contadores reales).
      await load()
    } catch (e) {
      // Rollback: restaura el byStage previo.
      setByStage(prevByStage)
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setPendingIds((s) => {
        const n = new Set(s)
        n.delete(opp.id)
        return n
      })
    }
  }

  async function confirmLost(reasonId: string) {
    if (!lostFor) return
    const opp = lostFor
    setLostFor(null)
    await doChangeStage(opp, 'PERDIDA', reasonId)
  }

  async function confirmWon() {
    if (!confirmWonFor) return
    const opp = confirmWonFor
    setConfirmWonFor(null)
    await doChangeStage(opp, 'GANADA')
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <Briefcase className="size-5 text-blueprint-700" />
            Pipeline comercial
          </h1>
          <p className="text-xs font-mono text-ink-700/60">
            9 stages espejo de JR2_OS · forecast ponderado por probabilidad · sin drag-drop,
            cambio por dropdown en cada card ·{' '}
            <Link to="/marketing/lost-reasons" className="text-blueprint-700 underline">
              gestionar razones de pérdida
            </Link>
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="bp-btn-primary">
          <Plus className="size-4" /> Nueva oportunidad
        </button>
      </header>

      <div className="bp-card p-3 mb-4 grid grid-cols-12 gap-2 items-center">
        <div className="col-span-6 relative">
          <Search className="size-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-700/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="buscar por título o cliente…"
            className="w-full pl-7 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            aria-label="buscar oportunidades"
          />
        </div>
        <select
          value={filterServiceLine}
          onChange={(e) => setFilterServiceLine(e.target.value)}
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
          aria-label="filtrar por línea de servicio"
        >
          <option value="">Todas las líneas</option>
          {/* F4: también las desactivadas, marcadas — permite aislar opps
              históricas de líneas retiradas en JR2_OS. */}
          {serviceLines.map((sl) => (
            <option key={sl.code} value={sl.code}>
              {sl.name}
              {!sl.active ? ' (inactiva)' : ''}
            </option>
          ))}
        </select>
        <label
          htmlFor="toggle-lost"
          className="col-span-3 flex items-center gap-2 text-xs font-mono text-ink-700/70 cursor-pointer"
        >
          <input
            id="toggle-lost"
            type="checkbox"
            checked={showLost}
            onChange={(e) => setShowLost(e.target.checked)}
          />
          mostrar perdidas
        </label>
      </div>

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}

      <div
        className="flex gap-3 overflow-x-auto pb-3"
        aria-live="polite"
        aria-label="columnas del pipeline"
      >
        {stagesToShow.map((stage) => {
          const bucket = filteredByStage[stage] ?? {
            items: [],
            hasMore: false,
            totalCount: 0,
            totalCents: 0,
          }
          const isTerminal = TERMINAL_STAGES.includes(stage)
          // Borde sutil para diferenciar terminales sin salirnos de Blueprint.
          const headerBorderClass =
            stage === 'GANADA'
              ? 'border-l-2 border-blueprint-700'
              : stage === 'PERDIDA'
                ? 'border-l-2 border-ink-700/30'
                : ''
          return (
            <section
              key={stage}
              className={`bp-card flex-shrink-0 w-56 flex flex-col bg-surface-alt/40 ${headerBorderClass}`}
              aria-label={`Columna ${STAGE_LABELS[stage]}`}
            >
              <header className="px-3 py-2 border-b border-ink-900/10">
                <div className="flex items-baseline justify-between">
                  <h2
                    className={`text-xs font-medium tracking-tight ${
                      isTerminal ? 'text-ink-700/70' : ''
                    }`}
                  >
                    {STAGE_LABELS[stage]}
                  </h2>
                  <span
                    className="bp-pill text-[10px]"
                    title={
                      filteringActive
                        ? 'Total del subconjunto filtrado (sobre las top 50 cargadas)'
                        : 'Total real del stage en el servidor'
                    }
                  >
                    {bucket.totalCount}
                  </span>
                </div>
                <div className="text-[11px] font-mono text-ink-700/60 mt-0.5">
                  {eur(bucket.totalCents)}
                  {filteringActive && (
                    <span
                      className="text-[10px] text-ink-700/40"
                      title="Count y suma calculados solo sobre las oportunidades que pasan el filtro"
                    >
                      {' '}
                      · filtrado
                    </span>
                  )}
                </div>
              </header>
              <div className="p-2 flex flex-col gap-2 overflow-y-auto max-h-[calc(100vh-280px)]">
                {bucket.items.length === 0 && (
                  <div className="text-[11px] text-ink-700/40 text-center py-4">
                    {/* Con filtros activos el vacío significa "nada pasa el
                        filtro", no "la etapa está vacía" — no mentir. */}
                    {filteringActive
                      ? 'Ninguna oportunidad coincide con el filtro en esta etapa'
                      : emptyStateText(stage)}
                    {stage === 'LEAD' && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          onClick={() => setCreating(true)}
                          className="text-blueprint-700 underline"
                        >
                          nueva
                        </button>
                      </>
                    )}
                  </div>
                )}
                {bucket.items.map((opp) => (
                  <article
                    key={opp.id}
                    className="bp-card bg-white px-2.5 py-2 flex flex-col gap-1.5"
                    aria-label={`Oportunidad ${opp.title}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <Link
                        to={`/marketing/opportunities/${opp.id}`}
                        className="text-sm font-medium text-blueprint-700 truncate flex-1"
                        title={opp.title}
                      >
                        {opp.title}
                      </Link>
                      <ChevronRight
                        className="size-3 text-ink-700/30 flex-shrink-0"
                        aria-hidden="true"
                      />
                    </div>
                    {opp.clientStub && (
                      <Link
                        to={`/marketing/clients/${opp.clientStub.id}`}
                        className="text-[11px] text-ink-700/70 truncate"
                      >
                        {opp.clientStub.displayName}
                      </Link>
                    )}
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-ink-900">{eur(opp.monetaryValueCents)}</span>
                      <span className="text-ink-700/50">{opp.probability}%</span>
                    </div>
                    {opp.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {opp.tags.slice(0, 3).map((t) => (
                          <span key={t} className="bp-pill text-[10px]">
                            {t}
                          </span>
                        ))}
                        {opp.tags.length > 3 && (
                          <span
                            className="bp-pill text-[10px]"
                            title={opp.tags.slice(3).join(', ')}
                          >
                            +{opp.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    <select
                      value={opp.stage}
                      onChange={(e) =>
                        void changeStage(opp, e.target.value as PipelineStage)
                      }
                      disabled={pendingIds.has(opp.id)}
                      className="text-[11px] border border-ink-900/15 rounded px-1 py-0.5 bg-white mt-1 disabled:opacity-50"
                      aria-label={`Cambiar stage de ${opp.title}`}
                    >
                      <optgroup label="Activos">
                        {ACTIVE_FLOW_STAGES.map((s) => (
                          <option key={s} value={s}>
                            {STAGE_LABELS[s]}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="En espera">
                        <option value="EN_ESPERA">{STAGE_LABELS.EN_ESPERA}</option>
                      </optgroup>
                      <optgroup label="Cerrar">
                        <option value="GANADA">{STAGE_LABELS.GANADA}</option>
                        <option value="PERDIDA">{STAGE_LABELS.PERDIDA}</option>
                      </optgroup>
                    </select>
                    {pendingIds.has(opp.id) && (
                      <div className="text-[10px] text-ink-700/40 font-mono">
                        guardando…
                      </div>
                    )}
                  </article>
                ))}
                {bucket.hasMore && (
                  <div className="text-[10px] text-ink-700/40 text-center py-1">
                    {filteringActive
                      ? 'puede haber más coincidencias fuera de las top 50 cargadas'
                      : `mostrando ${bucket.items.length} de ${bucket.totalCount} en este stage`}
                  </div>
                )}
              </div>
            </section>
          )
        })}
      </div>

      {forecast && (
        <footer className="bp-card mt-4 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50 flex items-center gap-1">
              <TrendingUp className="size-3.5" /> Pipeline total
            </div>
            <div className="text-sm font-medium leading-tight">
              {eur(forecast.totalPipelineCents)}
            </div>
            <div className="text-[10px] text-ink-700/50">{forecast.openCount} abiertas</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50">
              Forecast ponderado
            </div>
            <div className="text-sm font-medium leading-tight">
              {eur(forecast.weightedForecastCents)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50">
              Ganadas
            </div>
            <div className="text-sm font-medium leading-tight">{eur(forecast.wonCents)}</div>
            <div className="text-[10px] text-ink-700/50 leading-tight">
              {forecast.wonCount} cerradas · ticket medio{' '}
              {Number.isFinite(forecast.avgDealSizeCents)
                ? eur(forecast.avgDealSizeCents)
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50">
              Conversión
            </div>
            <div className="text-sm font-medium leading-tight">
              {Number.isFinite(forecast.conversionRate)
                ? `${(forecast.conversionRate * 100).toFixed(1)}%`
                : '—'}
            </div>
            <div className="text-[10px] text-ink-700/50 leading-tight">
              {forecast.wonCount} ganadas / {forecast.lostCount} perdidas
            </div>
          </div>
        </footer>
      )}

      {creating && (
        <CreateOpportunityModal
          serviceLines={serviceLines}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void load()
          }}
        />
      )}

      {lostFor && (
        <LostReasonModal
          reasons={lostReasons}
          onCancel={() => setLostFor(null)}
          onConfirm={confirmLost}
          oppTitle={lostFor.title}
        />
      )}

      {confirmWonFor && (
        <ConfirmStageModal
          oppTitle={confirmWonFor.title}
          toStageLabel="Ganada"
          message="Marcar como ganada cierra la oportunidad y dispara las TagRules y webhooks de cierre. ¿Confirmar?"
          onCancel={() => setConfirmWonFor(null)}
          onConfirm={confirmWon}
        />
      )}
    </div>
  )
}

interface ClientPickerResult {
  id: string
  displayName: string
}

function CreateOpportunityModal({
  serviceLines,
  onClose,
  onCreated,
}: {
  serviceLines: ServiceLine[]
  onClose: () => void
  onCreated: () => void
}) {
  const [title, setTitle] = useState('')
  const [valueEur, setValueEur] = useState('0')
  // Solo líneas ACTIVAS para crear (el catálogo del padre incluye inactivas
  // desde F4 porque el filtro del pipeline las necesita).
  const activeLines = serviceLines.filter((sl) => sl.active)
  // Default: la línea isDefault activa del catálogo; fallback contractual
  // CONSULTORIA_IA.
  const [serviceLine, setServiceLine] = useState<string>(
    () =>
      serviceLines.find((sl) => sl.isDefault && sl.active)?.code ??
      serviceLines.find((sl) => sl.active)?.code ??
      'CONSULTORIA_IA',
  )
  const [expected, setExpected] = useState('')
  const [clientQuery, setClientQuery] = useState('')
  const [clientResults, setClientResults] = useState<ClientPickerResult[]>([])
  const [clientId, setClientId] = useState<string | null>(null)
  const [clientName, setClientName] = useState<string>('')
  const [err, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [activeResultIdx, setActiveResultIdx] = useState(0)
  const firstInputRef = useRef<HTMLInputElement | null>(null)
  const pickerSeqRef = useRef(0)

  // Autofocus + cierre con Escape para accesibilidad teclado.
  useEffect(() => {
    firstInputRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    if (!clientQuery.trim()) {
      setClientResults([])
      return
    }
    const mySeq = ++pickerSeqRef.current
    const ac = new AbortController()
    const t = setTimeout(async () => {
      try {
        const r = await api<{ items: ClientPickerResult[] }>(
          `/v1/marketing/clients?q=${encodeURIComponent(clientQuery)}`,
          { signal: ac.signal },
        )
        if (mySeq !== pickerSeqRef.current) return
        setClientResults(r.items.slice(0, 8))
        setActiveResultIdx(0)
      } catch {
        if (mySeq !== pickerSeqRef.current) return
        setClientResults([])
      }
    }, 300)
    return () => {
      clearTimeout(t)
      ac.abort()
    }
  }, [clientQuery])

  async function submit() {
    setErr(null)
    if (!title.trim()) return setErr('Título obligatorio')
    if (!clientId) return setErr('Selecciona un cliente')
    const cents = Math.round(parseFloat(valueEur || '0') * 100)
    if (!Number.isFinite(cents) || cents < 0) return setErr('Valor inválido')
    setSubmitting(true)
    try {
      await api('/v1/marketing/opportunities', {
        method: 'POST',
        body: JSON.stringify({
          clientStubId: clientId,
          title: title.trim(),
          serviceLine,
          monetaryValueCents: isNaN(cents) ? 0 : cents,
          expectedCloseAt: expected ? new Date(expected).toISOString() : undefined,
        }),
      })
      onCreated()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  function pickClient(c: ClientPickerResult) {
    setClientId(c.id)
    setClientName(c.displayName)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-opp-title"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bp-card bg-white max-w-md w-full p-5">
        <h2 id="create-opp-title" className="text-base font-medium mb-3">
          Nueva oportunidad
        </h2>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-ink-700/60">Título</label>
            <input
              ref={firstInputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              placeholder="Consultoría inicial IA"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-ink-700/60">Cliente</label>
            {clientId ? (
              <div className="flex items-center justify-between text-sm border border-ink-900/15 rounded px-2 py-1.5">
                <span>{clientName}</span>
                <button
                  type="button"
                  onClick={() => {
                    setClientId(null)
                    setClientName('')
                    setClientQuery('')
                  }}
                  className="text-xs text-blueprint-700"
                >
                  cambiar
                </button>
              </div>
            ) : (
              <>
                <input
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (clientResults.length === 0) return
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setActiveResultIdx((i) =>
                        Math.min(i + 1, clientResults.length - 1),
                      )
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setActiveResultIdx((i) => Math.max(i - 1, 0))
                    } else if (e.key === 'Enter') {
                      e.preventDefault()
                      const c = clientResults[activeResultIdx]
                      if (c) pickClient(c)
                    }
                  }}
                  className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
                  placeholder="buscar cliente…"
                  aria-label="buscar cliente"
                />
                {clientResults.length > 0 && (
                  <ul
                    className="border border-ink-900/10 rounded mt-1 max-h-40 overflow-auto bg-white"
                    role="listbox"
                  >
                    {clientResults.map((c, idx) => (
                      <li key={c.id} role="option" aria-selected={idx === activeResultIdx}>
                        <button
                          type="button"
                          onClick={() => pickClient(c)}
                          onMouseEnter={() => setActiveResultIdx(idx)}
                          className={`w-full text-left px-2 py-1 text-sm ${
                            idx === activeResultIdx
                              ? 'bg-surface-alt'
                              : 'hover:bg-surface-alt'
                          }`}
                        >
                          {c.displayName}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono uppercase text-ink-700/60">
                Valor (EUR)
              </label>
              <input
                type="number"
                step="0.01"
                min={0}
                value={valueEur}
                onChange={(e) => setValueEur(e.target.value)}
                className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-ink-700/60">
                Línea de servicio
              </label>
              <select
                value={serviceLine}
                onChange={(e) => setServiceLine(e.target.value)}
                className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                {activeLines.length === 0 && (
                  <option value="CONSULTORIA_IA">Consultoría IA</option>
                )}
                {activeLines.map((sl) => (
                  <option key={sl.code} value={sl.code}>
                    {sl.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-ink-700/60">
              Cierre estimado
            </label>
            <input
              type="date"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {err}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="bp-btn">
            Cancelar
          </button>
          <button onClick={submit} disabled={submitting} className="bp-btn-primary">
            {submitting ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </div>
    </div>
  )
}

function LostReasonModal({
  reasons,
  onCancel,
  onConfirm,
  oppTitle,
}: {
  reasons: LostReason[]
  onCancel: () => void
  onConfirm: (id: string) => void
  oppTitle: string
}) {
  // Si reasons llega tarde, sincronizamos el select con la primera razón
  // disponible. Antes: reasonId quedaba '' y el botón disabled aunque el
  // select mostrase visualmente una opción.
  const [reasonId, setReasonId] = useState<string>(reasons[0]?.id ?? '')
  useEffect(() => {
    if (!reasonId && reasons[0]) setReasonId(reasons[0].id)
  }, [reasons, reasonId])

  // Cierre con Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lost-reason-title"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="bp-card bg-white max-w-md w-full p-5">
        <h2 id="lost-reason-title" className="text-base font-medium mb-1">
          Marcar como perdida
        </h2>
        <p className="text-xs text-ink-700/60 mb-3">{oppTitle}</p>
        {reasons.length === 0 ? (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            No hay razones disponibles. Crea alguna en{' '}
            <Link to="/marketing/lost-reasons" className="text-blueprint-700">
              Ajustes
            </Link>
            .
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-[10px] font-mono uppercase text-ink-700/60">
              Razón de pérdida
            </label>
            <select
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value)}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
              autoFocus
            >
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="bp-btn">
            Cancelar
          </button>
          <button
            onClick={() => reasonId && onConfirm(reasonId)}
            disabled={!reasonId}
            className="bp-btn-primary"
          >
            Confirmar pérdida
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmStageModal({
  oppTitle,
  toStageLabel,
  message,
  onCancel,
  onConfirm,
}: {
  oppTitle: string
  toStageLabel: string
  message: string
  onCancel: () => void
  onConfirm: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-stage-title"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="bp-card bg-white max-w-md w-full p-5">
        <h2 id="confirm-stage-title" className="text-base font-medium mb-1">
          Mover a {toStageLabel}
        </h2>
        <p className="text-xs text-ink-700/60 mb-3">{oppTitle}</p>
        <p className="text-sm text-ink-900 mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          {/* Foco inicial en Cancelar (política ConfirmDialog): un Enter
              distraído no debe cerrar la oportunidad. */}
          <button onClick={onCancel} className="bp-btn" autoFocus>
            Cancelar
          </button>
          <button onClick={onConfirm} className="bp-btn-primary">
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}
